import React from 'react';
import { Paragraph } from '../Paragraph/Paragraph';

type SizeLimiter<V> = V extends 'text' | 'ghost2'
  ? 'small'
  : keyof typeof buttonSizes;

type SubVariant<V> = V extends 'text' | 'ghost' | 'tertiary' ? boolean : never;

export type ButtonProps<V> = {
  text: string;
  variant?: V;
  subVariant?: SubVariant<V>;
  size?: SizeLimiter<V>;
} & (
  ({
    as?: 'button'
  } & React.ButtonHTMLAttributes<HTMLButtonElement>) |
  ({
    as?: 'a',
  } & React.AnchorHTMLAttributes<HTMLAnchorElement>))

export const buttonVariants = {
  primary: 'bg-yellow-primary text-neutrals-grey-950 is-desktop-detected:hover:bg-yellow-light',
  secondary:
    'bg-purple-primary text-neutrals-white radius-full is-desktop-detected:hover:bg-purple-light',
  tertiary:
    'bg-neutrals-grey-950 text-neutrals-white is-desktop-detected:hover:bg-neutrals-grey-500',
  light: 'bg-white text-neutrals-grey-950 is-desktop-detected:hover:bg-neutrals-coolGrey-50',
  ghost:
    'bg-transparent border-2 border-neutrals-grey-950 text-neutrals-grey-950 is-desktop-detected:hover:text-neutrals-grey-500 is-desktop-detected:hover:border-neutrals-grey-500',
  ghost2: 'bg-[rgba(255, 255, 255, 0.25)] text-neutrals-white', // Todo: clarify why this color is not defined in the design system?
  text: 'bg-transparent text-neutrals-grey-950 border-0 is-desktop-detected:hover:text-neutrals-grey-500',
} as const;

const subVariants = {
  tertiary: 'bg-neutrals-white',
  ghost: 'bg-transparent border-2 border-neutrals-white text-neutrals-white',
  ghost2: 'bg-[#28282833] text-neutrals-white', // Todo: clarify why this color is not defined in the design system?
  text: 'bg-transparent text-neutrals-white border-0',
} as const;

export const buttonSizes = {
  small: 'py-10 px-16',
  medium: 'py-14 px-24',
  large: 'py-20 px-40',
} as const;

export const baseStyle = 'family text-12 text-inherit rounded-100 uppercase font-extrabold border-0 border-solid focus-visible:ring-primary';
function getVariantStyles<V extends keyof typeof buttonVariants>(variant: V, subVariant?: SubVariant<V>, size: keyof typeof buttonSizes = 'large') {
  const variantStyle = buttonVariants[variant] || buttonVariants.primary;

  const subVariantStyle = subVariant && subVariants?.[variant as keyof typeof subVariants];

  const sizeStyle = buttonSizes[size] || buttonSizes.large;

  const appliedVariantStyle = subVariantStyle || variantStyle;

  return `${baseStyle} ${sizeStyle} ${appliedVariantStyle}`;
}

const getSize = (size: string) => {
  if (size === 'small') return 12;
  if (size === 'medium') return 14;
  return 16;
};

const getWeight = (variant: string) => {
  if (variant === 'text') return 'bold';
  return 'extrabold';
};

export const Button = <V extends keyof typeof buttonVariants>(props: ButtonProps<V>) => {
  const {
    variant = 'primary' as V,
    subVariant = false as SubVariant<V>,
    size = 'Large' as SizeLimiter<V>,
    className = '',
    text,
    onClick,
    as = 'button',
  } = props;

  const aTagProps = as === 'a' && 'href' in props ? { href: props.href } : {};

  const buttonStyling = getVariantStyles(variant, subVariant, size);

  const Tag: React.ElementType = as ?? 'button';

  return (
    <Tag
      {...aTagProps}
      // @ts-expect-error This can be both an <a> and a <button> which has different props
      onClick={onClick}

      className={`${buttonStyling} ${className} ${as === 'a' ? '!no-underline' : ''}`}
    >
      <Paragraph
        weight={getWeight(variant)}
        size={getSize(size)}
        className={variant === 'text' ? 'underline' : ''}
      >{text}</Paragraph>
    </Tag>
  );
};
